import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Car, Upload, Camera, CheckCircle, AlertCircle, FileText, X } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import { toast } from 'sonner';
import { compressImageFile } from '@/lib/imageCompress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LoanCarKiosk() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(10);
  
  // Helper to get local date string (YYYY-MM-DD) without timezone issues
  const getLocalDateString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper to get local time string (HH:MM) without timezone issues
  const getLocalTimeString = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };
  
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    customer_address: '',
    license_number: '',
    loan_car_rego: '',
    pickup_date: getLocalDateString(),
    pickup_time: getLocalTimeString()
  });
  
  // Get today's date for min attribute (local date)
  const today = getLocalDateString();
  
  // Get current time for validation
  const getCurrentTime = () => {
    return getLocalTimeString();
  };
  
  const [licenseImage, setLicenseImage] = useState(null);
  const [signature, setSignature] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  
  // Fleet cars for dropdown
  const [fleetCars, setFleetCars] = useState([]);
  const [fleetLoading, setFleetLoading] = useState(true);
  
  const signatureRef = useRef(null);
  const fileInputRef = useRef(null);

  // Fetch fleet cars for this site
  useEffect(() => {
    const fetchFleetCars = async () => {
      if (!siteId) return;
      
      setFleetLoading(true);
      try {
        const response = await fetch(`${BACKEND_URL}/api/loan-cars/fleet/available?site_id=${siteId}`);
        if (response.ok) {
          const data = await response.json();
          setFleetCars(data.fleet_cars || []);
        }
      } catch (error) {
        console.error('Failed to fetch fleet cars:', error);
      } finally {
        setFleetLoading(false);
      }
    };
    
    fetchFleetCars();
  }, [siteId]);

  // Auto-reset after successful submission
  useEffect(() => {
    if (submitted) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            // Reset the form for the next customer
            resetForm();
            return 10;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [submitted]);

  const resetForm = () => {
    setFormData({
      customer_name: '',
      customer_phone: '',
      customer_email: '',
      customer_address: '',
      license_number: '',
      loan_car_rego: '',
      pickup_date: getLocalDateString(),
      pickup_time: getLocalTimeString()
    });
    setLicenseImage(null);
    setSignature(null);
    setTermsAccepted(false);
    setSubmitted(false);
    setCountdown(10);
    setError(null);
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // If changing date to today, ensure time is not in the past
    if (name === 'pickup_date') {
      const todayDate = new Date().toISOString().split('T')[0];
      if (value === todayDate) {
        const currentTime = getCurrentTime();
        // If current pickup_time is before current time, update it
        if (formData.pickup_time < currentTime) {
          setFormData(prev => ({ ...prev, [name]: value, pickup_time: currentTime }));
          return;
        }
      }
    }
    
    // If changing time on today's date, don't allow past times
    if (name === 'pickup_time') {
      const todayDate = new Date().toISOString().split('T')[0];
      if (formData.pickup_date === todayDate) {
        const currentTime = getCurrentTime();
        if (value < currentTime) {
          toast.error('Cannot select a time in the past');
          return;
        }
      }
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const [compressingPhoto, setCompressingPhoto] = useState(false);

  const handleLicenseUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCompressingPhoto(true);
    try {
      const compressed = await compressImageFile(file);
      setLicenseImage(compressed);
    } catch (err) {
      console.error('Photo compression failed:', err);
      toast.error('Could not process photo. Please try a different one.');
    } finally {
      setCompressingPhoto(false);
    }
  };

  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
      setSignature(null);
    }
  };

  const confirmSignature = () => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      setSignature(signatureRef.current.toDataURL('image/png'));
      toast.success('Signature captured');
    } else {
      toast.error('Please draw your signature first');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name || !formData.customer_phone) {
      toast.error('Please fill in your name and phone number');
      return;
    }
    
    if (!formData.loan_car_rego) {
      toast.error('Please enter the loan car registration number');
      return;
    }
    
    // Validate pickup datetime is not in the past
    const pickupDatetime = new Date(`${formData.pickup_date}T${formData.pickup_time}`);
    const now = new Date();
    if (pickupDatetime < now) {
      toast.error('Pickup date and time cannot be in the past');
      return;
    }
    
    if (!signature) {
      toast.error('Please provide your signature');
      return;
    }
    
    if (!termsAccepted) {
      toast.error('Please accept the terms and conditions');
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Combine pickup date and time
      const pickupDatetimeStr = `${formData.pickup_date}T${formData.pickup_time}:00`;
      
      const response = await fetch(`${BACKEND_URL}/api/loan-cars/kiosk-submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          site_id: siteId,
          customer_name: formData.customer_name,
          customer_phone: formData.customer_phone,
          customer_email: formData.customer_email,
          customer_address: formData.customer_address,
          license_number: formData.license_number,
          loan_car_rego: formData.loan_car_rego.toUpperCase(),
          pickup_datetime: pickupDatetimeStr,
          license_image: licenseImage,
          signature: signature,
          terms_accepted: termsAccepted
        })
      });
      
      if (response.ok) {
        setSubmitted(true);
        toast.success('Loan car details submitted successfully!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit details');
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to submit details. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Success screen after submission
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-12 w-12 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Thank You!</h1>
          <p className="text-lg text-slate-600 mb-6">
            Your loan car details have been submitted successfully. Our team will assist you shortly.
          </p>
          <div className="bg-slate-100 rounded-lg p-4 mb-6">
            <p className="text-slate-500">Resetting for next customer in</p>
            <p className="text-4xl font-bold text-blue-600">{countdown}s</p>
          </div>
          <Button onClick={resetForm} variant="outline" className="w-full">
            Start New Form Now
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Car className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900">Loan Car Check-In</h1>
          <p className="text-slate-600 mt-2">Please fill in your details to collect the loan car</p>
        </div>

        {/* Form */}
        <Card className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Loan Car Rego */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <Label htmlFor="loan_car_rego" className="text-blue-800 font-semibold">
                Select Loan Car *
              </Label>
              {fleetLoading ? (
                <div className="mt-2 py-3 text-center text-slate-500">Loading available cars...</div>
              ) : fleetCars.length > 0 ? (
                <Select
                  value={formData.loan_car_rego}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, loan_car_rego: value }))}
                >
                  <SelectTrigger className="mt-2 text-lg">
                    <SelectValue placeholder="Select a loan car" />
                  </SelectTrigger>
                  <SelectContent>
                    {fleetCars.map((car) => (
                      <SelectItem key={car.fleet_id} value={car.rego}>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold">{car.rego}</span>
                          <span className="text-slate-700">
                            {car.vehicle_name || `${car.make || ''} ${car.model || ''}`.trim() || 'Unnamed'}
                          </span>
                          {car.status === 'on_loan' && (
                            <span className="text-amber-600 text-xs">(On loan)</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="loan_car_rego"
                  name="loan_car_rego"
                  value={formData.loan_car_rego}
                  onChange={handleInputChange}
                  placeholder="e.g., ABC123"
                  className="mt-2 text-lg font-mono uppercase"
                  required
                />
              )}
              <p className="text-sm text-blue-600 mt-1">
                {fleetCars.length > 0 
                  ? 'Select the loan car you are collecting' 
                  : 'Enter the rego of the loan car you are collecting'}
              </p>
            </div>

            {/* Pickup Date & Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="pickup_date" className="font-semibold">Pickup Date *</Label>
                <Input
                  id="pickup_date"
                  name="pickup_date"
                  type="date"
                  value={formData.pickup_date}
                  onChange={handleInputChange}
                  min={today}
                  className="mt-1"
                  required
                />
              </div>
              <div>
                <Label htmlFor="pickup_time" className="font-semibold">Pickup Time *</Label>
                <Input
                  id="pickup_time"
                  name="pickup_time"
                  type="time"
                  value={formData.pickup_time}
                  onChange={handleInputChange}
                  className="mt-1"
                  required
                />
              </div>
            </div>

            {/* Customer Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="customer_name">Full Name *</Label>
                <Input
                  id="customer_name"
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={handleInputChange}
                  placeholder="John Smith"
                  className="mt-1"
                  required
                />
              </div>
              <div>
                <Label htmlFor="customer_phone">Phone Number *</Label>
                <Input
                  id="customer_phone"
                  name="customer_phone"
                  value={formData.customer_phone}
                  onChange={handleInputChange}
                  placeholder="0412 345 678"
                  className="mt-1"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="customer_email">Email</Label>
                <Input
                  id="customer_email"
                  name="customer_email"
                  type="email"
                  value={formData.customer_email}
                  onChange={handleInputChange}
                  placeholder="john@example.com"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="license_number">License Number</Label>
                <Input
                  id="license_number"
                  name="license_number"
                  value={formData.license_number}
                  onChange={handleInputChange}
                  placeholder="License number"
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="customer_address">Address</Label>
              <Input
                id="customer_address"
                name="customer_address"
                value={formData.customer_address}
                onChange={handleInputChange}
                placeholder="123 Main St, Melbourne VIC 3000"
                className="mt-1"
              />
            </div>

            {/* License Photo Upload */}
            <div>
              <Label className="mb-2 block">Driver's License Photo (Optional)</Label>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center">
                {licenseImage ? (
                  <div className="relative inline-block">
                    <img src={licenseImage} alt="License" className="max-h-40 rounded-lg" data-testid="loan-kiosk-license-preview" />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute -top-2 -right-2 h-6 w-6"
                      onClick={() => setLicenseImage(null)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : compressingPhoto ? (
                  <div className="py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent mx-auto mb-2" />
                    <p className="text-slate-500 text-sm">Optimising photo...</p>
                  </div>
                ) : (
                  <div>
                    <Camera className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-slate-500 text-sm mb-2">Take a photo of your license</p>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="loan-kiosk-upload-license-btn"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Photo
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLicenseUpload}
                      className="hidden"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Signature */}
            <div>
              <Label className="mb-2 block">Your Signature *</Label>
              <div className="border border-slate-300 rounded-lg overflow-hidden bg-white">
                <SignatureCanvas
                  ref={signatureRef}
                  canvasProps={{
                    className: 'w-full h-40',
                    style: { width: '100%', height: '160px' }
                  }}
                  backgroundColor="white"
                />
              </div>
              <div className="flex gap-2 mt-2">
                <Button type="button" variant="outline" size="sm" onClick={clearSignature}>
                  Clear
                </Button>
                <Button type="button" variant="secondary" size="sm" onClick={confirmSignature}>
                  {signature ? '✓ Confirmed' : 'Confirm Signature'}
                </Button>
              </div>
              {signature && (
                <p className="text-sm text-green-600 mt-1 flex items-center gap-1">
                  <CheckCircle className="h-4 w-4" />
                  Signature captured
                </p>
              )}
            </div>

            {/* Terms & Conditions */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="terms"
                  checked={termsAccepted}
                  onCheckedChange={(checked) => setTermsAccepted(checked)}
                />
                <div>
                  <Label htmlFor="terms" className="cursor-pointer">
                    I acknowledge that I have read and agree to the{' '}
                    <button
                      type="button"
                      onClick={() => setShowTermsModal(true)}
                      className="text-blue-600 hover:underline font-medium"
                    >
                      Courtesy Vehicle Terms & Conditions
                    </button>
                  </Label>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full h-14 text-lg bg-blue-600 hover:bg-blue-700"
              disabled={submitting || compressingPhoto}
              data-testid="loan-kiosk-submit-btn"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2" />
                  Submitting...
                </>
              ) : (
                'Submit & Collect Loan Car'
              )}
            </Button>
          </form>
        </Card>

        {/* Terms Modal */}
        <Dialog open={showTermsModal} onOpenChange={setShowTermsModal}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Courtesy Vehicle Terms & Conditions
              </DialogTitle>
              <DialogDescription>
                Please read these terms carefully before accepting
              </DialogDescription>
            </DialogHeader>
            <div className="prose prose-sm max-w-none text-slate-700 space-y-4">
              <h3 className="font-semibold text-slate-900">1. Vehicle Use</h3>
              <p>The courtesy vehicle is provided solely for the use of the named driver. The vehicle must not be used for any illegal purpose, racing, or off-road driving.</p>
              
              <h3 className="font-semibold text-slate-900">2. Driver Requirements</h3>
              <p>The driver must hold a valid Australian driver's license appropriate for the vehicle class. The driver must be over 21 years of age unless otherwise agreed.</p>
              
              <h3 className="font-semibold text-slate-900">3. Insurance</h3>
              <p>Basic insurance coverage is provided. The driver is responsible for any excess payable in the event of an accident or damage. Additional coverage may be available upon request.</p>
              
              <h3 className="font-semibold text-slate-900">4. Fuel Policy</h3>
              <p>The vehicle is provided with a reasonable amount of fuel. Please return the vehicle with a similar fuel level.</p>
              
              <h3 className="font-semibold text-slate-900">5. Vehicle Condition</h3>
              <p>Please report any existing damage before taking the vehicle. You are responsible for any new damage that occurs during your use.</p>
              
              <h3 className="font-semibold text-slate-900">6. Return</h3>
              <p>The vehicle must be returned by the agreed date and time. Late returns may incur additional charges.</p>
              
              <h3 className="font-semibold text-slate-900">7. Smoking & Pets</h3>
              <p>Smoking is prohibited in all courtesy vehicles. Pets are allowed only with prior approval and appropriate containment.</p>
              
              <h3 className="font-semibold text-slate-900">8. Liability</h3>
              <p>The borrower accepts full responsibility for any fines, tolls, or penalties incurred during the loan period.</p>
            </div>
            <div className="mt-4">
              <Button onClick={() => setShowTermsModal(false)} className="w-full">
                I Understand
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
